import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import * as styles from './TranslateModal.module.scss'

import { SUPPORTED_LANGUAGES } from '../../../i18n'
import { Col, Container, Modal, ModalProps, Row, Text } from '../../basics'

const translateNamespace = 'components.TranslateModal'

// 切り替えがこの時間より早く終わる場合はスピナーを出さない (チラつき防止)。
// 遅い (キャッシュなし等) 場合だけ閾値を超えてスピナーが表示される (#1134)。
const SPINNER_DELAY_MS = 300

type TranslateModalProps = Pick<ModalProps, 'isOpen' | 'onClose'>

export const TranslateModal: React.FC<TranslateModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t, i18n } = useTranslation()
  // 言語切り替えは重い再レンダリングをともない、特にキャッシュがない状態では
  // 切り替わるまで無反応に感じる。一方で速い切り替えでスピナーが一瞬出ると
  // かえってチラついて不自然なため、一定時間 (SPINNER_DELAY_MS) を超えて
  // まだ切り替え中のときだけスピナーを表示する (#1134)。
  //
  // changeLanguage は imperative な呼び出しで、再レンダリングは react-i18next の
  // 購読 (languageChanged) 経由で届くため useTransition の isPending では捕捉
  // できない。よって切り替え中の言語を自前の state で持ち、その Promise の解決で
  // 解除する。スピナー表示自体はさらに setTimeout の閾値で遅延させる。
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null)
  const [showSpinner, setShowSpinner] = useState(false)
  const isMountedRef = useRef(true)
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSpinnerTimer = () => {
    if (spinnerTimerRef.current !== null) {
      clearTimeout(spinnerTimerRef.current)
      spinnerTimerRef.current = null
    }
  }

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      clearSpinnerTimer()
    }
  }, [])

  const handleSelectLanguage = (language: string) => {
    if (pendingLanguage) return
    // 現在と同じ言語をクリックした場合は切り替え不要なので何もしない。
    if (i18n.language === language) return

    setPendingLanguage(language)
    // 閾値を超えてもまだ切り替え中ならスピナーを表示する。
    spinnerTimerRef.current = setTimeout(() => {
      spinnerTimerRef.current = null
      if (isMountedRef.current) {
        setShowSpinner(true)
      }
    }, SPINNER_DELAY_MS)

    i18n.changeLanguage(language).finally(() => {
      clearSpinnerTimer()
      if (isMountedRef.current) {
        setPendingLanguage(null)
        setShowSpinner(false)
      }
    })
  }

  const isPending = pendingLanguage !== null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={<Text>{t(`${translateNamespace}.selectLanguage`)}</Text>}
      subTitle={<Text>{t(`${translateNamespace}.selectLanguageSub`)}</Text>}
    >
      <div className={styles.languages}>
        <Container>
          <Row>
            {SUPPORTED_LANGUAGES.map((language) => (
              <Col
                sp={12}
                pc={6}
                className={styles.translateCol}
                key={`translateModal-${language}`}
              >
                <button
                  className={clsx(
                    styles.langButton,
                    i18n.language === language && styles.langButtonSelected
                  )}
                  type="button"
                  disabled={isPending}
                  onClick={() => {
                    handleSelectLanguage(language)
                  }}
                >
                  <Text inline weight="bold" size={18}>
                    <div className={styles.languageName}>
                      {t(`${translateNamespace}.language.${language}`)}
                      {showSpinner && pendingLanguage === language && (
                        <span
                          className={styles.spinner}
                          data-testid="language-spinner"
                          aria-hidden
                        />
                      )}
                    </div>
                  </Text>
                </button>
              </Col>
            ))}
          </Row>
        </Container>
      </div>
    </Modal>
  )
}
